import { isPresent } from '@luminovo/commons';
import { SecondaryIconButton } from '@luminovo/design-system';
import { Close } from '@mui/icons-material';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

export enum DialogLevel {
    Level1 = 'level1',
    Level2 = 'level2',
    Level3 = 'level3',
    Dynamic = 'dynamic',
}

const ModalContext = React.createContext<
    | {
          level1: {
              dialog: JSX.Element | null;
              setDialog: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
          };
          level2: {
              dialog: JSX.Element | null;
              setDialog: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
          };
          level3: {
              dialog: JSX.Element | null;
              setDialog: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
          };
          drawer: JSX.Element | null;
          setDrawer: React.Dispatch<React.SetStateAction<JSX.Element | null>>;
      }
    | undefined
>(undefined);

/**
 * Hook for managing modal dialogs with support for multiple layers/levels.
 *
 * The dialog system supports three levels of modals that can be stacked:
 * - level1: Base level dialog, used for primary/main modal content
 * - level2: Secondary level dialog, can be opened on top of level1 for nested workflows
 * - level3: Tertiary level dialog, highest level for critical confirmations or final steps
 *
 * Each level can only have one dialog open at a time. Opening a dialog at a specific
 * level when that level already has an open dialog will be prevented.
 *
 * The hook manages dialog state using refs to track the current and target dialog levels.
 * It automatically determines the appropriate level for new dialogs based on what's already
 * open, and provides methods to set, close individual dialogs, or close all dialogs at once.
 */
export function useDialogContext(defaultLevel: DialogLevel = DialogLevel.Level1) {
    const modalContext = React.useContext(ModalContext);
    if (!modalContext) {
        throw new Error("ModalContext is not present, make sure you've setup the ModalContext.Provider correctly.");
    }

    const { level1, level2, level3 } = modalContext;
    const currentLevelRef = React.useRef<DialogLevel | null>(null);
    const targetLevel = React.useRef<DialogLevel | null>(null);

    React.useEffect(() => {
        if (isPresent(currentLevelRef.current)) {
            return;
        }

        if (defaultLevel !== DialogLevel.Dynamic) {
            targetLevel.current = defaultLevel;
            return;
        }

        if (!isPresent(level1.dialog)) {
            targetLevel.current = DialogLevel.Level1;
        } else if (!isPresent(level2.dialog)) {
            targetLevel.current = DialogLevel.Level2;
        } else if (!isPresent(level3.dialog)) {
            targetLevel.current = DialogLevel.Level3;
        }
    }, [defaultLevel, level1, level2, level3]);

    const setDialog = React.useCallback(
        (dialog: JSX.Element) => {
            if (targetLevel.current === DialogLevel.Level1) {
                currentLevelRef.current = DialogLevel.Level1;
                level1.setDialog(dialog);
            } else if (targetLevel.current === DialogLevel.Level2) {
                currentLevelRef.current = DialogLevel.Level2;
                level2.setDialog(dialog);
            } else if (targetLevel.current === DialogLevel.Level3) {
                currentLevelRef.current = DialogLevel.Level3;
                level3.setDialog(dialog);
            } else {
                // Default back to level1
                level3.setDialog(null);
                level2.setDialog(null);
                level1.setDialog(dialog);
                currentLevelRef.current = DialogLevel.Level1;
            }
        },
        [level1, level2, level3],
    );

    const closeDialog = React.useCallback(() => {
        if (currentLevelRef.current === DialogLevel.Level1) {
            level1.setDialog(null);
        } else if (currentLevelRef.current === DialogLevel.Level2) {
            level2.setDialog(null);
        } else if (currentLevelRef.current === DialogLevel.Level3) {
            level3.setDialog(null);
        }
        currentLevelRef.current = null;
        targetLevel.current = null;
    }, [level1, level2, level3]);

    const closeAllDialogs = React.useCallback(() => {
        level1.setDialog(null);
        level2.setDialog(null);
        level3.setDialog(null);
        currentLevelRef.current = null;
        targetLevel.current = null;
    }, [level1, level2, level3]);

    return {
        setDialog,
        closeDialog,
        closeAllDialogs,
        isOpen: isPresent(currentLevelRef.current),
    };
}

export function useDrawerContext() {
    const modalContext = React.useContext(ModalContext);
    if (!modalContext) {
        throw new Error("ModalContext is not present, make sure you've setup the ModalContext.Provider correctly.");
    }

    const { setDrawer, drawer } = modalContext;
    return { setDrawer, closeDrawer: () => setDrawer(null), isOpen: isPresent(drawer) };
}

export const CloseDrawerButton: React.FunctionComponent<{ disableMargin?: boolean }> = ({
    disableMargin,
}): JSX.Element => {
    const { closeDrawer } = useDrawerContext();

    return (
        <SecondaryIconButton
            size="small"
            onClick={() => closeDrawer()}
            style={{ margin: disableMargin ? undefined : '12px 24px 12px 16px' }}
        >
            <Close fontSize="inherit" />
        </SecondaryIconButton>
    );
};

export function ModalContextProvider({ children }: React.PropsWithChildren<{}>) {
    const [dialogLevel1, setDialogLevel1] = React.useState<JSX.Element | null>(null);
    const [dialogLevel2, setDialogLevel2] = React.useState<JSX.Element | null>(null);
    const [dialogLevel3, setDialogLevel3] = React.useState<JSX.Element | null>(null);
    const [drawer, setDrawer] = React.useState<JSX.Element | null>(null);

    const { pathname } = useLocation();

    React.useEffect(() => {
        setDialogLevel1(null);
        setDialogLevel2(null);
        setDialogLevel3(null);
        setDrawer(null);
    }, [pathname]);

    return (
        <ModalContext.Provider
            value={{
                level1: { dialog: dialogLevel1, setDialog: setDialogLevel1 },
                level2: { dialog: dialogLevel2, setDialog: setDialogLevel2 },
                level3: { dialog: dialogLevel3, setDialog: setDialogLevel3 },
                drawer,
                setDrawer,
            }}
        >
            <>
                {dialogLevel3}
                {dialogLevel2}
                {dialogLevel1}
                {drawer}
                {children}
            </>
        </ModalContext.Provider>
    );
}
